import isPlainObject from 'lodash/isPlainObject';
import isFunction from 'lodash/isFunction';
import isArray from 'lodash/isArray';
import isNull from 'lodash/isNull';
import keys from 'lodash/keys';
import isUndefined from 'lodash/isUndefined';

import type { Brand } from '../types/brand';
import type { System } from '../types/system';
import { getThemeValue, hasDefault, THEME_DEFAULT_KEY } from './getTheme';
import positiveOrNegative from './positiveOrNegative';
import getObjectWithVariants from './getObjectWithVariants';
import cssScales from './cssScales';

type CSSTransformKeys =
  | 'margin'
  | 'marginTop'
  | 'marginRight'
  | 'marginBottom'
  | 'marginLeft'
  | 'marginBlock'
  | 'marginBlockEnd'
  | 'marginBlockStart'
  | 'marginInline'
  | 'marginInlineEnd'
  | 'marginInlineStart'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right';

type CSSTransformObj = {
  [Key in CSSTransformKeys]: string | number;
};

const defaultBreakpoints = [40, 52, 64].map(n => n + 'em');

const defaultTheme = {
  space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
  fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 72],
};

const cssTransformKeys = [
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginBlock',
  'marginBlockEnd',
  'marginBlockStart',
  'marginInline',
  'marginInlineEnd',
  'marginInlineStart',
  'top',
  'bottom',
  'left',
  'right',
].reduce<CSSTransformObj>(
  (previous, currentKey) => ({
    ...previous,
    [currentKey]: positiveOrNegative,
  }),
  {} as CSSTransformObj,
);

export const responsive =
  <T extends Brand = Brand>(styles: Exclude<System.StyleObject<T>, System.CSSDerivedStyles<T>>) =>
  (theme?: T) => {
    const breakpointsKeys = theme && theme?.breakpoints && Object.keys(theme.breakpoints || {});
    const breakpoints: string[] = breakpointsKeys?.length
      ? breakpointsKeys.map(breakpoint => theme?.breakpoints[breakpoint].min)
      : defaultBreakpoints;

    const mediaQueries = [
      null,
      ...breakpoints.map(breakpoint =>
        breakpoint?.includes('@media') ? breakpoint : `@media screen and (min-width: ${breakpoint})`,
      ),
    ];

    return (keys(styles) as Array<keyof typeof styles>).reduce<
      Exclude<System.StyleObject<T>, System.CSSDerivedStyles<T>>
    >((previous, currentKey) => {
      let currentItem = styles[currentKey];

      if (isFunction(currentItem)) {
        currentItem = currentItem(theme || {});
      }

      if (isNull(currentItem) || isUndefined(currentItem) || (!currentItem && currentItem !== 0)) return previous;

      if (!isArray(currentItem)) {
        return {
          ...previous,
          [currentKey]: currentItem,
        };
      }

      if (isArray(currentItem)) {
        const currentMediaItems = currentItem.slice(0, mediaQueries.length) as any[];

        const currentArrayItem = currentMediaItems.reduce<Exclude<System.StyleObject<T>, System.CSSDerivedStyles<T>>>(
          (previousItem, itemValue, index) => {
            const currentMedia = mediaQueries[index];

            if (isNull(currentMedia))
              return {
                ...previousItem,
                [currentKey]: itemValue,
              };

            if (isNull(itemValue)) return previousItem;

            return {
              ...previousItem,
              [currentMedia]: {
                ...(previous?.[currentMedia] || {}),
                ...(previousItem?.[currentMedia] || {}),
                [currentKey]: itemValue,
              },
            };
          },
          {},
        );

        return {
          ...previous,
          ...currentArrayItem,
        };
      }

      return previous;
    }, {});
  };

export const css =
  <Theme extends Brand>(args: System.StyleObject<Theme> = {}) =>
  (props: { theme: Brand } | Brand = { theme: {} as Brand }): System.CSSObject => {
    const theme = { ...defaultTheme, ...('theme' in props ? props.theme : props) } as Theme;

    const obj = getObjectWithVariants(isFunction(args) ? args(theme) : args, theme);
    const styles = responsive(obj as System.CSSObject)(theme);

    return keys(styles).reduce<System.CSSObject>((previous, currentKey) => {
      const cssStyle = styles[currentKey as keyof typeof styles];
      const cssValue = isFunction(cssStyle) ? cssStyle(theme) : cssStyle;

      if (currentKey === 'variant') {
        const themeValue = getThemeValue(theme, cssValue);
        const variant = css(themeValue)(theme);

        return {
          ...previous,
          ...variant,
        };
      }

      if (cssValue && isPlainObject(cssValue)) {
        if (hasDefault(cssValue)) {
          return {
            ...previous,
            [currentKey]: cssValue[THEME_DEFAULT_KEY],
          };
        }

        // On type level, val can also be an array here,
        // but we transform all arrays in `responsive` function.
        return {
          ...previous,
          [currentKey]: css(cssValue)(theme),
        };
      }

      // Take the currentKey and get the current css key value for it
      const currentCSSKey = getThemeValue(theme, currentKey, currentKey);
      // Check if the currentKey has a scale to pull values from
      const scaleName = getThemeValue(cssScales, currentKey, currentKey);

      const scale = getThemeValue(theme, scaleName, getThemeValue(theme, currentCSSKey));
      // @ts-ignore
      const transform = getThemeValue(cssTransformKeys, currentCSSKey, getThemeValue);
      const value = transform(scale, cssValue, cssValue);

      if (!isUndefined(currentCSSKey)) {
        return {
          ...previous,
          [currentCSSKey]: value,
        };
      }

      return previous;
    }, {});
  };

export default css;
